import React from "react";
import withStyles from "@material-ui/core/styles/withStyles";
import * as yup from "yup";
import produce from "immer";
import {
  AuthConsumer,
  PropertyForm,
  SubmitButton,
  requiredEmail,
  requiredString,
  transformErrors
} from "../../common";
import Box from "@material-ui/core/Box";

const registrationForm = {
  section: { marginTop: 1 },
  field: { padding: 1 },
  hideCancel: true,
  hideReset: true,
  hideSave: true,
  transformPutRequest: produce(request => ({
    email: request.email,
    password: request.password,
    name: {
      first: request.first_name,
      last: request.last_name
    },
    timezone:
      request.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
  })),
  defaultValues: {
    email: null,
    first_name: null,
    last_name: null,
    password: null,
    confirm: null,
    timezone: "Placeholder"
  },
  validationSchema: yup.object().shape({
    first_name: requiredString,
    last_name: requiredString,
    email: requiredEmail,
    password: requiredString,
    confirm: requiredString.oneOf(
      [yup.ref("password"), null],
      "Passwords must match"
    )
  }),
  fieldInfo: {
    email: {
      autoFocus: true,
      label: "Email Address",
      autoComplete: "email",
      fullWidth: true,
      required: true,
      xs: 12
    },
    first_name: {
      label: "First Name",
      autoComplete: "given-name",
      required: true,
      fullWidth: true,
      xs: 12,
      sm: 6
    },
    last_name: {
      label: "Last Name",
      autoComplete: "family-name",
      required: true,
      fullWidth: true,
      xs: 12,
      sm: 6
    },
    password: {
      label: "Password",
      autoComplete: "new-password",
      type: "password",
      required: true,
      fullWidth: true,
      xs: 12,
      sm: 6
    },
    confirm: {
      label: "Confirm Password",
      autoComplete: "new-password",
      type: "password",
      required: true,
      fullWidth: true,
      xs: 12,
      sm: 6
    }
  },
  sections: [
    {
      layout: [["email"], ["first_name", "last_name"], ["password", "confirm"]]
    }
  ]
};

const RegisterForm = withStyles(
  theme => ({
    submit: {
      padding: theme.spacing(1)
    }
  }),
  { name: "RegisterForm", withTheme: true }
)(({ classes, onEmailExists }) => (
  <AuthConsumer>
    {({ register, refreshAuth }) => {
      function onSubmit(vals, form) {
        const transformPutRequest =
          registrationForm.transformPutRequest || (i => i);
        const request = transformPutRequest(vals);

        register(request, result => {
          if (result.success) {
            form.setStatus(undefined);
            form.setErrors({});
            refreshAuth({ username: vals.email, password: vals.password }, () =>
              form.setSubmitting(false)
            );
          } else {
            form.setSubmitting(false);
            if (typeof result.message === "string")
              form.setStatus(result.message);
            else {
              const errors = transformErrors(result.message);
              if (errors.password) {
                const actualPasswordErrors = [];
                errors.password.forEach(err => {
                  for (let [key, value] of Object.entries(err)) {
                    actualPasswordErrors.push(key + " " + value);
                  }
                });
                errors.password = actualPasswordErrors.map((item, idx) => (
                  <React.Fragment key={idx}>
                    {item}
                    <br />
                  </React.Fragment>
                ));
              }
              form.setErrors(errors);

              if (onEmailExists && errors.email === "Email exists")
                onEmailExists(request.email);
            }
          }
        });
      }

      return (
        <PropertyForm {...registrationForm} onSubmit={onSubmit}>
          <Box m={1}>
            <SubmitButton
              variant="contained"
              color="primary"
              className={classes.submit}
            >
              Begin
            </SubmitButton>
          </Box>
        </PropertyForm>
      );
    }}
  </AuthConsumer>
));

export default RegisterForm;
